import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    FormControl,
    Typography,
    Select,
    MenuItem,
    InputLabel,
    CircularProgress,
    InputAdornment,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { UserProfileData } from '../UserProfileForm';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';

interface StepLocationLanguageProps {
    data: Partial<UserProfileData>;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    error?: string;
}

export const StepLocationLanguage: React.FC<StepLocationLanguageProps> = ({ data, onChange, error }) => {
    const { t } = useTranslation();
    const [detecting, setDetecting] = useState(false);

    useEffect(() => {
        const detectLocation = async () => {
            // Only try to detect if location is empty
            if (!data.location || data.location.trim() === '') {
                setDetecting(true);
                try {
                    const getCurrentCity = httpsCallable(functions, 'getCurrentCity');
                    const result = await getCurrentCity();
                    const typedResult = result.data as { city?: string; country?: string };
                    
                    if (typedResult.city) {
                        // Double check it's still empty before setting
                        // (User might have started typing while fetch was running)
                        if (!data.location || data.location.trim() === '') {
                            onChange({
                                target: {
                                    name: 'location',
                                    value: typedResult.city
                                }
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }
                } catch (err) {
                    console.warn("Failed to detect location from IP", err);
                } finally {
                    setDetecting(false);
                }
            }
        };

        detectLocation();
    }, [data.location, onChange]);

    return (
        <Box>
            <Typography variant="h6" gutterBottom sx={{ textAlign: 'left' }}>
                {t('onboarding.location_title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'left' }}>
                {t('onboarding.location_subtitle')}
            </Typography>

            <TextField
                label={t('settings.location_label')}
                name="location"
                value={data.location || ''}
                onChange={onChange}
                fullWidth
                margin="normal"
                required
                error={!!error}
                helperText={error || ''}
                InputProps={{
                    endAdornment: detecting ? (
                        <InputAdornment position="end">
                            <CircularProgress size={20} color="inherit" />
                        </InputAdornment>
                    ) : null,
                }}
            />

            <Box sx={{ mt: 3, mb: 2 }}>
                <FormControl fullWidth>
                    <InputLabel id="language-select-label">{t('settings.app_language')}</InputLabel>
                    <Select
                        labelId="language-select-label"
                        id="language-select"
                        name="language"
                        value={data.language || 'en'}
                        label={t('settings.app_language')}
                        onChange={onChange as any}
                        sx={{ textAlign: 'left' }}
                    >
                        <MenuItem value="en">English</MenuItem>
                        <MenuItem value="de">Deutsch</MenuItem>
                        <MenuItem value="fr">Français</MenuItem>
                    </Select>
                </FormControl>
            </Box>
        </Box>
    );
};
